'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
export type TooltipAlign = 'center' | 'start' | 'end';

interface TooltipTriggerProps {
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
}

interface TooltipProps {
    content: React.ReactNode;
    children: React.ReactElement<TooltipTriggerProps>;
    placement?: TooltipPlacement;
    align?: TooltipAlign;
    disabled?: boolean;
    className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
    content,
    children,
    placement = 'top',
    align = 'center',
    disabled = false,
    className = ''
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [actualPlacement, setActualPlacement] = useState(placement);
    const [actualAlign, setActualAlign] = useState(align);
    const [isPositioned, setIsPositioned] = useState(false);
    const triggerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate distances from edges
        const distanceFromRight = viewportWidth - triggerRect.right;
        const distanceFromLeft = triggerRect.left;
        const distanceFromTop = triggerRect.top;
        const distanceFromBottom = viewportHeight - triggerRect.bottom;

        let top = 0;
        let left = 0;
        let finalAlign = align;
        let finalPlacement = placement;

        // Auto-adjust alignment based on horizontal space (like old tooltip logic)
        if (placement === 'top' || placement === 'bottom') {
            // Check if we need to adjust horizontal alignment
            if (distanceFromRight < 50 && align === 'center') {
                // Too close to right edge - align to end
                finalAlign = 'end';
            } else if (distanceFromLeft < 100 && align === 'center') {
                // Too close to left edge - align to start
                finalAlign = 'start';
            }
        }

        // Auto-adjust placement based on vertical space
        if (placement === 'top' && distanceFromTop < tooltipRect.height + 20) {
            // Not enough space above - switch to bottom
            finalPlacement = 'bottom';
        } else if (placement === 'bottom' && distanceFromBottom < tooltipRect.height + 20) {
            // Not enough space below - switch to top
            finalPlacement = 'top';
        }

        // Calculate position based on final placement and alignment
        switch (finalPlacement) {
            case 'top':
                top = triggerRect.top - tooltipRect.height - 8;
                left = finalAlign === 'center'
                    ? triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
                    : finalAlign === 'start'
                    ? triggerRect.left
                    : triggerRect.right - tooltipRect.width;
                break;

            case 'bottom':
                top = triggerRect.bottom + 8;
                left = finalAlign === 'center'
                    ? triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
                    : finalAlign === 'start'
                    ? triggerRect.left
                    : triggerRect.right - tooltipRect.width;
                break;

            case 'left':
                left = triggerRect.left - tooltipRect.width - 8;
                top = finalAlign === 'center'
                    ? triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
                    : finalAlign === 'start'
                    ? triggerRect.top
                    : triggerRect.bottom - tooltipRect.height;
                break;

            case 'right':
                left = triggerRect.right + 8;
                top = finalAlign === 'center'
                    ? triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2
                    : finalAlign === 'start'
                    ? triggerRect.top
                    : triggerRect.bottom - tooltipRect.height;
                break;
        }

        // Final boundary checks - prevent tooltip from going off-screen
        const padding = 8;
        if (left < padding) left = padding;
        if (left + tooltipRect.width > viewportWidth - padding) {
            left = viewportWidth - tooltipRect.width - padding;
        }
        if (top < padding) top = padding;
        if (top + tooltipRect.height > viewportHeight - padding) {
            top = viewportHeight - tooltipRect.height - padding;
        }

        setPosition({ top, left });
        setActualPlacement(finalPlacement);
        setActualAlign(finalAlign);
        setIsPositioned(true);
    }, [placement, align]);

    useEffect(() => {
        if (isVisible) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isVisible, updatePosition]);

    const handleMouseEnter = () => {
        if (!disabled) {
            setIsVisible(true);
        }
    };

    const handleMouseLeave = () => {
        setIsVisible(false);
        setIsPositioned(false);
    };

    const getArrowClassName = () => {
        const baseClass = styles.tooltipArrow;
        const placementClass = styles[`tooltipArrow_${actualPlacement}`];
        const alignClass = styles[`tooltipArrow_${actualAlign}`];
        return `${baseClass} ${placementClass} ${alignClass}`;
    };

    // Clone children and add event handlers.
    // The callback ref below only runs when React attaches/detaches the trigger
    // node (outside of render), but the analyzer cannot see through the legacy
    // cloneElement API, so the refs rule is disabled for this line.
    // TECH_DEBT.md: migrate Tooltip away from cloneElement to remove this.
    // eslint-disable-next-line react-hooks/refs -- callback ref, runs outside render
    const clonedChildren = React.cloneElement(children, {
        ref: (node: HTMLElement | null) => {
            triggerRef.current = node;
        },
        onMouseEnter: (e: React.MouseEvent) => {
            handleMouseEnter();
            // Call original onMouseEnter if exists
            if (children.props.onMouseEnter) {
                children.props.onMouseEnter(e);
            }
        },
        onMouseLeave: (e: React.MouseEvent) => {
            handleMouseLeave();
            // Call original onMouseLeave if exists
            if (children.props.onMouseLeave) {
                children.props.onMouseLeave(e);
            }
        },
    });

    return (
        <>
            {clonedChildren}

            {mounted && isVisible && !disabled && createPortal(
                <div
                    ref={tooltipRef}
                    className={`${styles.tooltip} ${className}`}
                    style={{
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        opacity: isPositioned ? 1 : 0
                    }}
                >
                    {content}
                    <div className={getArrowClassName()} />
                </div>,
                document.body
            )}
        </>
    );
};

export default Tooltip;

